/**
 * useSynthAmbient - Synthesized ambient sound hook
 * Manages pad, drone, and nature sound generation
 */
import { useState, useRef, useCallback } from 'react';
import { buildFxBus, buildPinkNoise, buildBrownNoise, buildWhiteNoise } from '../lib/audioFx';

// Available synth ambient presets
export const SYNTH_PRESETS = {
  // Pads & Drones
  'warm-pad': { name: '🎹 Warm Pad', category: 'Pads', description: 'Soft, warm evolving pad' },
  'dark-drone': { name: '🌑 Dark Drone', category: 'Pads', description: 'Deep, mysterious drone' },
  'ethereal-pad': { name: '✨ Ethereal Pad', category: 'Pads', description: 'Light, airy atmosphere' },
  'cosmic-drift': { name: '🌌 Cosmic Drift', category: 'Pads', description: 'Space-like evolving texture' },
  'zen-drone': { name: '🕉️ Zen Drone', category: 'Pads', description: 'Meditative harmonic drone' },
  'choir-pad': { name: '👼 Choir Pad', category: 'Pads', description: 'Vocal-like harmonics' },
  // Nature
  'ocean-waves': { name: '🌊 Ocean Waves', category: 'Nature', description: 'Rhythmic wave patterns' },
  'rain-storm': { name: '🌧️ Rain Storm', category: 'Nature', description: 'Gentle rain ambience' },
  'wind-howl': { name: '💨 Wind', category: 'Nature', description: 'Soft wind movement' },
  // Tonal
  'singing-bowl': { name: '🔔 Singing Bowl', category: 'Tonal', description: 'Tibetan bowl resonance' },
  'crystal-tones': { name: '💎 Crystal Tones', category: 'Tonal', description: 'Pure crystalline harmonics' },
  // Noise — for focus masking & sleep (iteration 25)
  'pink-noise':  { name: '🌸 Pink Noise',  category: 'Noise', description: 'Warmest masking for focus & sleep' },
  'brown-noise': { name: '🟫 Brown Noise', category: 'Noise', description: 'Deep rumble, masks traffic / snoring' },
  'white-noise': { name: '⚪ White Noise', category: 'Noise', description: 'Flat-spectrum, pure sound masking' },
};

/**
 * @param audioContextRef  React ref to the AudioContext
 * @param outputNodeRef    Optional ref to a destination AudioNode. If provided
 *                         the synth's FX bus connects to this node instead of
 *                         ctx.destination — used by the parent to insert a
 *                         recording tap upstream so MP3/WebM exports actually
 *                         contain the synth ambient (iter 25.5 fix).
 */
export const useSynthAmbient = (audioContextRef, outputNodeRef = null) => {
  // State
  const [isEnabled, setIsEnabled] = useState(false);
  const [currentType, setCurrentType] = useState('warm-pad');
  const [volume, setVolume] = useState([0.5]);

  // Refs
  const nodesRef = useRef(null);
  const mainGainRef = useRef(null);

  // Create synth nodes based on type
  const createSynthNodes = useCallback((type) => {
    const ctx = audioContextRef.current;
    if (!ctx) return null;

    const mainGain = ctx.createGain();
    mainGain.gain.value = 0;
    mainGainRef.current = mainGain;

    const nodes = { oscillators: [], gains: [], filters: [], lfos: [], mainGain };

    switch (type) {
      case 'warm-pad': {
        const baseFreqs = [110, 220, 330];
        baseFreqs.forEach((freq, i) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          const filter = ctx.createBiquadFilter();
          
          osc.type = 'sine';
          osc.frequency.value = freq;
          osc.detune.value = (Math.random() - 0.5) * 5;
          
          filter.type = 'lowpass';
          filter.frequency.value = 500;
          filter.Q.value = 0.5;
          
          gain.gain.value = 0.12 / (i + 1);
          
          osc.connect(filter);
          filter.connect(gain);
          gain.connect(mainGain);
          
          nodes.oscillators.push(osc);
          nodes.gains.push(gain);
          nodes.filters.push(filter);
        });
        
        // Filter LFO
        const filterLfo = ctx.createOscillator();
        const filterLfoGain = ctx.createGain();
        filterLfo.type = 'sine';
        filterLfo.frequency.value = 0.03;
        filterLfoGain.gain.value = 200;
        filterLfo.connect(filterLfoGain);
        nodes.filters.forEach(f => filterLfoGain.connect(f.frequency));
        nodes.lfos.push(filterLfo);
        break;
      }

      case 'dark-drone': {
        const baseFreqs = [55, 110, 165];
        baseFreqs.forEach((freq, i) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          const filter = ctx.createBiquadFilter();
          
          osc.type = 'sine';
          osc.frequency.value = freq;
          osc.detune.value = (Math.random() - 0.5) * 4;
          
          filter.type = 'lowpass';
          filter.frequency.value = 250;
          filter.Q.value = 0.5;
          
          gain.gain.value = 0.15 / (i + 1);
          
          osc.connect(filter);
          filter.connect(gain);
          gain.connect(mainGain);
          
          nodes.oscillators.push(osc);
          nodes.gains.push(gain);
          nodes.filters.push(filter);
        });
        
        const ampLfo = ctx.createOscillator();
        const ampLfoGain = ctx.createGain();
        ampLfo.type = 'sine';
        ampLfo.frequency.value = 0.05;
        ampLfoGain.gain.value = 0.03;
        ampLfo.connect(ampLfoGain);
        ampLfoGain.connect(mainGain.gain);
        nodes.lfos.push(ampLfo);
        break;
      }

      case 'ethereal-pad': {
        const baseFreqs = [261.6, 392, 523.2];
        baseFreqs.forEach((freq, i) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          const filter = ctx.createBiquadFilter();
          
          osc.type = 'sine';
          osc.frequency.value = freq;
          osc.detune.value = (Math.random() - 0.5) * 6;
          
          filter.type = 'lowpass';
          filter.frequency.value = 800;
          filter.Q.value = 0.3;
          
          gain.gain.value = 0.1 / (i + 1);
          
          osc.connect(filter);
          filter.connect(gain);
          gain.connect(mainGain);
          
          nodes.oscillators.push(osc);
          nodes.gains.push(gain);
          nodes.filters.push(filter);
        });
        
        const shimmerLfo = ctx.createOscillator();
        const shimmerGain = ctx.createGain();
        shimmerLfo.type = 'sine';
        shimmerLfo.frequency.value = 0.1;
        shimmerGain.gain.value = 0.02;
        shimmerLfo.connect(shimmerGain);
        shimmerGain.connect(mainGain.gain);
        nodes.lfos.push(shimmerLfo);
        break;
      }

      case 'cosmic-drift': {
        const baseFreqs = [82.4, 164.8, 247];
        baseFreqs.forEach((freq, i) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          const filter = ctx.createBiquadFilter();
          
          osc.type = 'sine';
          osc.frequency.value = freq;
          osc.detune.value = (Math.random() - 0.5) * 10;
          
          filter.type = 'lowpass';
          filter.frequency.value = 400;
          filter.Q.value = 0.3;
          
          gain.gain.value = 0.12 / (i + 1);
          
          osc.connect(filter);
          filter.connect(gain);
          gain.connect(mainGain);
          
          nodes.oscillators.push(osc);
          nodes.gains.push(gain);
          nodes.filters.push(filter);
        });
        
        const driftLfo = ctx.createOscillator();
        const driftGain = ctx.createGain();
        driftLfo.type = 'sine';
        driftLfo.frequency.value = 0.02;
        driftGain.gain.value = 150;
        driftLfo.connect(driftGain);
        nodes.filters.forEach(f => driftGain.connect(f.frequency));
        nodes.lfos.push(driftLfo);
        break;
      }

      case 'zen-drone': {
        const omFreq = 136.1;
        const intervals = [1, 1.5, 2];
        
        intervals.forEach((mult, i) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          const filter = ctx.createBiquadFilter();
          
          osc.type = 'sine';
          osc.frequency.value = omFreq * mult;
          osc.detune.value = (Math.random() - 0.5) * 3;
          
          filter.type = 'lowpass';
          filter.frequency.value = 400;
          filter.Q.value = 0.3;
          
          gain.gain.value = 0.15 / (i + 1);
          
          osc.connect(filter);
          filter.connect(gain);
          gain.connect(mainGain);
          
          nodes.oscillators.push(osc);
          nodes.gains.push(gain);
          nodes.filters.push(filter);
        });
        
        const breathLfo = ctx.createOscillator();
        const breathGain = ctx.createGain();
        breathLfo.type = 'sine';
        breathLfo.frequency.value = 0.04;
        breathGain.gain.value = 0.025;
        breathLfo.connect(breathGain);
        breathGain.connect(mainGain.gain);
        nodes.lfos.push(breathLfo);
        break;
      }

      case 'choir-pad': {
        const baseFreqs = [196, 247, 294];
        
        baseFreqs.forEach((freq, i) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          const filter = ctx.createBiquadFilter();
          
          osc.type = 'sine';
          osc.frequency.value = freq;
          osc.detune.value = (Math.random() - 0.5) * 5;
          
          filter.type = 'bandpass';
          filter.frequency.value = 700;
          filter.Q.value = 2;
          
          gain.gain.value = 0.1 / (i + 1);
          
          osc.connect(filter);
          filter.connect(gain);
          gain.connect(mainGain);
          
          nodes.oscillators.push(osc);
          nodes.gains.push(gain);
          nodes.filters.push(filter);
        });
        
        const morphLfo = ctx.createOscillator();
        const morphGain = ctx.createGain();
        morphLfo.type = 'sine';
        morphLfo.frequency.value = 0.02;
        morphGain.gain.value = 200;
        morphLfo.connect(morphGain);
        nodes.filters.forEach(f => morphGain.connect(f.frequency));
        nodes.lfos.push(morphLfo);
        break;
      }

      case 'ocean-waves': {
        const bufferSize = ctx.sampleRate * 2;
        const noiseBuffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
        const data = noiseBuffer.getChannelData(0);
        
        let lastOut = 0;
        for (let i = 0; i < bufferSize; i++) {
          const white = Math.random() * 2 - 1;
          data[i] = (lastOut + (0.02 * white)) / 1.02;
          lastOut = data[i];
          data[i] *= 3.5;
        }
        
        const noise = ctx.createBufferSource();
        noise.buffer = noiseBuffer;
        noise.loop = true;
        
        const filter = ctx.createBiquadFilter();
        filter.type = 'lowpass';
        filter.frequency.value = 400;
        
        const gain = ctx.createGain();
        gain.gain.value = 0.25;
        
        const waveLfo = ctx.createOscillator();
        const waveLfoGain = ctx.createGain();
        waveLfo.type = 'sine';
        waveLfo.frequency.value = 0.1;
        waveLfoGain.gain.value = 200;
        waveLfo.connect(waveLfoGain);
        waveLfoGain.connect(filter.frequency);
        
        noise.connect(filter);
        filter.connect(gain);
        gain.connect(mainGain);
        
        nodes.oscillators.push(noise);
        nodes.lfos.push(waveLfo);
        nodes.filters.push(filter);
        nodes.gains.push(gain);
        break;
      }

      case 'rain-storm': {
        const bufferSize = ctx.sampleRate * 2;
        const noiseBuffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
        const data = noiseBuffer.getChannelData(0);
        
        let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
        for (let i = 0; i < bufferSize; i++) {
          const white = Math.random() * 2 - 1;
          b0 = 0.99886 * b0 + white * 0.0555179;
          b1 = 0.99332 * b1 + white * 0.0750759;
          b2 = 0.96900 * b2 + white * 0.1538520;
          b3 = 0.86650 * b3 + white * 0.3104856;
          b4 = 0.55000 * b4 + white * 0.5329522;
          b5 = -0.7616 * b5 - white * 0.0168980;
          data[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362) * 0.11;
          b6 = white * 0.115926;
        }
        
        const noise = ctx.createBufferSource();
        noise.buffer = noiseBuffer;
        noise.loop = true;
        
        const highpass = ctx.createBiquadFilter();
        highpass.type = 'highpass';
        highpass.frequency.value = 1000;
        
        const lowpass = ctx.createBiquadFilter();
        lowpass.type = 'lowpass';
        lowpass.frequency.value = 8000;
        
        const gain = ctx.createGain();
        gain.gain.value = 0.3;
        
        noise.connect(highpass);
        highpass.connect(lowpass);
        lowpass.connect(gain);
        gain.connect(mainGain);
        
        nodes.oscillators.push(noise);
        nodes.filters.push(highpass, lowpass);
        nodes.gains.push(gain);
        break;
      }

      case 'wind-howl': {
        const bufferSize = ctx.sampleRate * 2;
        const noiseBuffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
        const data = noiseBuffer.getChannelData(0);
        
        for (let i = 0; i < bufferSize; i++) {
          data[i] = Math.random() * 2 - 1;
        }
        
        const noise = ctx.createBufferSource();
        noise.buffer = noiseBuffer;
        noise.loop = true;
        
        const bandpass = ctx.createBiquadFilter();
        bandpass.type = 'bandpass';
        bandpass.frequency.value = 600;
        bandpass.Q.value = 2;
        
        const gain = ctx.createGain();
        gain.gain.value = 0.15;
        
        const gustLfo = ctx.createOscillator();
        const gustLfoGain = ctx.createGain();
        gustLfo.type = 'sine';
        gustLfo.frequency.value = 0.15;
        gustLfoGain.gain.value = 400;
        gustLfo.connect(gustLfoGain);
        gustLfoGain.connect(bandpass.frequency);
        
        noise.connect(bandpass);
        bandpass.connect(gain);
        gain.connect(mainGain);
        
        nodes.oscillators.push(noise);
        nodes.lfos.push(gustLfo);
        nodes.filters.push(bandpass);
        nodes.gains.push(gain);
        break;
      }

      case 'singing-bowl': {
        const baseFreq = 256;
        const harmonics = [1, 2.76, 5.4];
        
        harmonics.forEach((mult, i) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          
          osc.type = 'sine';
          osc.frequency.value = baseFreq * mult;
          osc.detune.value = (Math.random() - 0.5) * 5;
          
          gain.gain.value = 0.1 / (i + 1);
          
          osc.connect(gain);
          gain.connect(mainGain);
          
          nodes.oscillators.push(osc);
          nodes.gains.push(gain);
        });
        break;
      }

      case 'crystal-tones': {
        const baseFreq = 523.25;
        const intervals = [1, 1.26, 1.5];
        
        intervals.forEach((mult, i) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          
          osc.type = 'sine';
          osc.frequency.value = baseFreq * mult;
          
          const shimmerLfo = ctx.createOscillator();
          const shimmerGain = ctx.createGain();
          shimmerLfo.type = 'sine';
          shimmerLfo.frequency.value = 0.3 + (Math.random() * 0.4);
          shimmerGain.gain.value = 0.02;
          shimmerLfo.connect(shimmerGain);
          shimmerGain.connect(gain.gain);
          nodes.lfos.push(shimmerLfo);
          
          gain.gain.value = 0.08;
          
          osc.connect(gain);
          gain.connect(mainGain);
          
          nodes.oscillators.push(osc);
          nodes.gains.push(gain);
        });
        break;
      }

      // ─── Noise presets (iter 25) ────────────────────────────
      // All three share the same architecture: stereo noise buffer → gentle
      // lowpass to tame harsh top-end → gain → master. FX bus adds reverb + warmth.
      case 'pink-noise':
      case 'brown-noise':
      case 'white-noise': {
        const buf = type === 'pink-noise'  ? buildPinkNoise(ctx, 4)
                  : type === 'brown-noise' ? buildBrownNoise(ctx, 4)
                                           : buildWhiteNoise(ctx, 4);
        const src = ctx.createBufferSource();
        src.buffer = buf;
        src.loop = true;

        const filter = ctx.createBiquadFilter();
        filter.type = 'lowpass';
        // Brown already rolls off hard — open the filter wider. White needs more taming.
        filter.frequency.value = type === 'white-noise' ? 6000
                               : type === 'pink-noise'  ? 9000
                                                        : 12000;
        filter.Q.value = 0.5;

        const gain = ctx.createGain();
        // Perceived-loudness match — brown noise's low-freq energy is louder per RMS
        gain.gain.value = type === 'brown-noise' ? 0.32
                        : type === 'pink-noise'  ? 0.35
                                                 : 0.22;

        src.connect(filter);
        filter.connect(gain);
        gain.connect(mainGain);

        nodes.oscillators.push(src);
        nodes.filters.push(filter);
        nodes.gains.push(gain);
        break;
      }

      default:
        return null;
    }

    return nodes;
  }, [audioContextRef]);

  // Stop synth
  const stop = useCallback(() => {
    const ctx = audioContextRef.current;
    const nodes = nodesRef.current;

    if (!nodes) return;

    if (ctx && nodes.mainGain) {
      const currentTime = ctx.currentTime;
      // Smooth fade out using setTargetAtTime
      nodes.mainGain.gain.cancelScheduledValues(currentTime);
      nodes.mainGain.gain.setTargetAtTime(0, currentTime, 0.1);

      setTimeout(() => {
        nodes.oscillators?.forEach(osc => {
          try { osc.stop(); osc.disconnect(); } catch (e) {}
        });
        nodes.lfos?.forEach(lfo => {
          try { lfo.stop(); lfo.disconnect(); } catch (e) {}
        });
        nodes.filters?.forEach(f => {
          try { f.disconnect(); } catch (e) {}
        });
        nodes.gains?.forEach(g => {
          try { g.disconnect(); } catch (e) {}
        });
        try { nodes.mainGain.disconnect(); } catch (e) {}
        // Disconnect FX bus nodes so the graph can be garbage-collected
        try { nodes.fxInput?.disconnect(); } catch (e) {}
        try { nodes.fxOutput?.disconnect(); } catch (e) {}
      }, 600);
    }

    nodesRef.current = null;
    setIsEnabled(false);
    console.log('🎹 Synth ambient stopped');
  }, [audioContextRef]);

  // Start synth
  const start = useCallback((type = currentType) => {
    const ctx = audioContextRef.current;
    if (!ctx) return;

    // Resume AudioContext if suspended (synth can start independently of beats)
    if (ctx.state === 'suspended') {
      ctx.resume();
    }

    stop(); // Stop any existing

    const nodes = createSynthNodes(type);
    if (!nodes) return;

    nodesRef.current = nodes;

    // iter 25: route through the shared FX bus — convolution reverb (stereo
    // IR provides natural width) → soft saturation. Dramatic sonic upgrade
    // over the previous "direct to destination" routing. The Haas widener
    // was removed in 25.1 because it was biasing the image leftward on
    // mono sources — reverb alone delivers balanced stereo width.
    const fx = buildFxBus(ctx, {
      wet: type.includes('noise') ? 0.08 : 0.35,  // less reverb on noise (muddy otherwise)
      warmth: 0.3,
    });
    nodes.fxInput = fx.input;
    nodes.fxOutput = fx.output;
    nodes.mainGain.connect(fx.input);
    // Route through parent-supplied bus when available so the recording tap
    // can capture the synth. Falls back to direct destination connect to keep
    // the hook usable in isolation.
    const target = outputNodeRef?.current || ctx.destination;
    fx.output.connect(target);

    // Start all oscillators and LFOs
    const currentTime = ctx.currentTime;
    nodes.oscillators.forEach(osc => {
      try { osc.start(currentTime); } catch (e) {}
    });
    nodes.lfos?.forEach(lfo => {
      try { lfo.start(currentTime); } catch (e) {}
    });

    // Fade in using setTargetAtTime for click-free start
    nodes.mainGain.gain.setValueAtTime(0, currentTime);
    nodes.mainGain.gain.setTargetAtTime(volume[0], currentTime, 0.5);

    setIsEnabled(true);
    setCurrentType(type);
    console.log(`🎹 Synth ambient started: ${type} → destination (isolated from beats)`);
  }, [audioContextRef, currentType, volume, createSynthNodes, stop]);

  // Update volume
  const updateVolume = useCallback((newVolume) => {
    setVolume(newVolume);
    if (nodesRef.current?.mainGain && audioContextRef.current) {
      const ctx = audioContextRef.current;
      const now = ctx.currentTime;
      // Use setTargetAtTime for click-free volume changes
      nodesRef.current.mainGain.gain.cancelScheduledValues(now);
      nodesRef.current.mainGain.gain.setTargetAtTime(newVolume[0], now, 0.02);
    }
  }, [audioContextRef]);

  // Change type
  const changeType = useCallback((newType) => {
    setCurrentType(newType);
    if (isEnabled) {
      stop();
      setTimeout(() => start(newType), 200);
    }
  }, [isEnabled, start, stop]);

  // Toggle
  const toggle = useCallback(() => {
    if (isEnabled) {
      stop();
    } else {
      start();
    }
  }, [isEnabled, start, stop]);

  return {
    // State
    isEnabled,
    currentType,
    volume,
    presets: SYNTH_PRESETS,
    
    // Methods
    start,
    stop,
    toggle,
    updateVolume,
    changeType,
    
    // Refs
    nodesRef,
    mainGainRef,
  };
};

export default useSynthAmbient;
